import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Wheel } from "react-custom-roulette";
import "./App.css";
import confetti from "canvas-confetti";
import { useLocation, useNavigate } from "react-router-dom";
import AdminLogin from "./components/AdminLogin/AdminLogin";
import AIChat from "./components/AIChat/AIChat";
import {
  validateUser,
  fetchUserData,
  fetchSegments,
  saveCssChanges,
  fetchCarkCss,
  resetToDefaultCss,
  fetchUserHistory,
  spinWheel,
  sendSpinCountRequest,
} from "./services/userService";
import { isAdminRoute } from "./helper/apiHelper";
import Loading from "./components/Loading/Loading";
import WheelEditor from "./components/WheelEditor/WheelEditor";
import DesignEditor from "./components/DesignEditor/DesignEditor";
import AdminButtons from "./components/AdminButtons/AdminButtons";
import LoadingSpinner from "./components/LoadingSpinner/LoadingSpinner";
import CustomAlert from "./components/CustomAlert/CustomAlert";
import ThemeSelector from "./components/ThemeSelector/ThemeSelector";
import NoSpinAlert from "./components/NoSpinAlert/NoSpinAlert";
import ResetConfirmationModal from "./components/ResetConfirmationModal";

// Pointer PNG baz rengi: H≈9°, S≈78%, L≈53.6%
const hexToPointerFilter = (hex) => {
  if (!hex || hex.length < 7) return '';
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const lit = (max + min) / 2;
  let hue = 0, sat = 0;
  if (max !== min) {
    const d = max - min;
    sat = lit > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) * 60;
    else if (max === g) hue = ((b - r) / d + 2) * 60;
    else hue = ((r - g) / d + 4) * 60;
  }
  const hueRot = Math.round((hue - 9 + 360) % 360);
  const satFactor = Math.round(sat / 0.78 * 100);
  const briFactor = Math.round(lit / 0.536 * 100);
  return `hue-rotate(${hueRot}deg) saturate(${satFactor}%) brightness(${briFactor}%)`;
};

// RGB değerlerini hesaplayan yardımcı fonksiyon
const getRGB = (hex) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#") || hex.length !== 7) {
    return "9, 249, 132";
  }
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return "9, 249, 132";
    return `${r}, ${g}, ${b}`;
  } catch (error) {
    console.error("Renk dönüşüm hatası:", error);
    return "9, 249, 132";
  }
};

// Hex renk kodundan hue (ton) derecesi çıkar
const hexToHue = (hex) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#") || hex.length < 7) return 0;
  try {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let hue = 0;
    if (max !== min) {
      const d = max - min;
      if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) * 60;
      else if (max === g) hue = ((b - r) / d + 2) * 60;
      else hue = ((r - g) / d + 4) * 60;
    }
    return hue;
  } catch { return 0; }
};

function App() {
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [showWheelEditor, setShowWheelEditor] = useState(false);
  const [showDesignEditor, setShowDesignEditor] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [showAIChat, setShowAIChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("Kullanıcı");
  const [spinCount, setSpinCount] = useState(0);
  const [status, setStatus] = useState(false);
  const [segments, setSegments] = useState([]);
  const [invalidAccess, setInvalidAccess] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [logoSrc, setLogoSrc] = useState("/default-logo.png");
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showNoSpinAlert, setShowNoSpinAlert] = useState(false);
  const [isRequestingSpinCount, setIsRequestingSpinCount] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [activeBackground, setActiveBackground] = useState(null);

  // CSS değişikliklerini takip etmek için yeni bir state
  const [cssState, setCssState] = useState({
    backgroundColor1: "#1A2234",
    backgroundColor2: "#2E4057",
    textColor1: "#E0E6ED",
    textColor2: "#09F984",
    borderColor: "#1A2234",
    pointerColor: "#09F984",
    wheelGlowColor: "#09F984",
    wheelGlowOpacity: 0.2,
    radiusLineColor: "#46868F",
    buttonGradient1: "#1A2234",
    buttonGradient2: "#2E4057",
    buttonTextColor: "#ffffff",
    buttonBorderColor: "#3B82F6",
    buttonGlowColor: "#3B82F6",
    buttonDisabled1: "#2a4a4d",
    buttonDisabled2: "#1F3C3F",
    buttonDisabledText: "#4a6a6d",
    buttonDisabledBorder: "#2a4a4d",
    headerBg: "rgba(30, 41, 59, 0.7)",
    headerBorderColor: "rgba(255, 255, 255, 0.1)",
    backgroundGradient1: "#0F172A",
    backgroundGradient2: "#1E293B",
    headerItemBg: "rgba(15, 23, 42, 0.6)",
    headerItemText: "#ffffff",
    headerItemBorder: "rgba(255, 255, 255, 0.1)",
    headerItemHoverBg: "rgba(15, 23, 42, 0.8)",
    headerItemHoverBorder: "rgba(9, 249, 132, 0.3)",
    headerEmojiColor: "#09F984",
    headerIconColor: "#09F984",
    headerTextColor: "#ffffff",
    headerButtonBg: "rgba(30, 41, 59, 0.7)",
    headerButtonHoverBg: "rgba(15, 23, 42, 0.8)",
    // Yeni modal renkleri
    modalBg: "#1A2234",
    modalBorderColor: "#09F984",
    modalHeaderColor: "#FFFFFF",
    modalTextColor: "#E0E6ED",
    modalButtonGradient1: "#1A2234",
    modalButtonGradient2: "#2E4057",
    modalButtonText: "#FFFFFF",
    // Geçmiş modal renkleri
    historyModalBg: "#1A2234",
    historyModalBorder: "#09F984",
    historyHeaderColor: "#FFFFFF",
    historyCloseColor: "#FFFFFF",
    historyDateColor: "#94A3B8",
    historyBadgeBg: "#09F984",
    historyBadgeText: "#1A2234",
    historyInfoLabel: "#94A3B8",
    historyInfoValue: "#E2E8F0",
    historyNoteColor: "#94A3B8",
    historyItemBg: "#2E4057",
    historyItemBorder: "#09F984",
    historyHeaderBg: "#1A2234",
    // No Spin Alert renkleri
    noSpinOverlayBg: "rgba(15, 23, 42, 0.95)",
    noSpinModalGradient1: "#1A2234",
    noSpinModalGradient2: "#2E4057",
    noSpinModalBorder: "rgba(255, 255, 255, 0.1)",
    noSpinModalTitle: "#FFFFFF",
    noSpinModalText: "rgba(255, 255, 255, 0.8)",
    // Dekorasyon renkleri
    ringAccentColor: "#FFD700",
    ambientGlowColor: "#FFD700",
    standAccentColor: "#C8960A",
    cloverColor: "#FFD700",
    // Arkaplan görseli
    customBgEnabled: false,
    customBgImage: null,
  });

  const [isLoading, setIsLoading] = useState(true);

  // State ekleyelim
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // useRef ekleyelim
  const wheelSoundRef = useRef(new Audio("/wheelsound.mp3"));
  const winSoundRef = useRef(new Audio("/win.mp3"));

  // CSS değişikliklerini kaydetme fonksiyonu
  const handleSaveCssChanges = async () => {
    try {
      setIsLoading(true); // Loading göster
      const result = await saveCssChanges(cssState);

      if (result.success) {
        // Wheel glow özelliklerini doğrudan uygula (kayıt sonrası)
        if (cssState.wheelGlowColor && typeof cssState.wheelGlowOpacity === "number") {
          try {
            const wheelGlowRgba = `rgba(${getRGB(cssState.wheelGlowColor)}, ${
              cssState.wheelGlowOpacity
            })`;
            document.querySelectorAll(".wheel-container").forEach((container) => {
              container.style.filter = `drop-shadow(0 0 50px ${wheelGlowRgba})`;
            });
          } catch (error) {
            console.error("Kayıt sonrası wheel glow güncellenirken hata:", error);
          }
        }

        setAlertConfig({
          show: true,
          message: "CSS değişiklikleri başarıyla kaydedildi",
          type: "success",
        });
      } else {
        setAlertConfig({
          show: true,
          message: result.message || "CSS değişiklikleri kaydedilemedi",
          type: "error",
        });
      }
    } catch (error) {
      console.error("CSS kaydetme hatası:", error);
      setAlertConfig({
        show: true,
        message: "CSS değişiklikleri kaydedilirken bir hata oluştu",
        type: "error",
      });
    } finally {
      setIsLoading(false); // Loading'i kaldır
    }
  };

  useEffect(() => {
    const setBackgroundStyle = () => {
      const isMobile = window.innerWidth <= 768;

      setActiveBackground(
        isMobile ? "images/bg-image-mobile.webp" : "images/bg-image.webp"
      );
    };

    setBackgroundStyle();
    window.addEventListener("resize", setBackgroundStyle);

    return () => window.removeEventListener("resize", setBackgroundStyle);
  }, []);

  // Çark verilerini API'den al
  useEffect(() => {
    const loadSegments = async () => {
      const segmentData = await fetchSegments();
      if (segmentData) {
        setSegments(segmentData);
      }
    };

    loadSegments();
  }, []);

  const data = useMemo(() => {
    if (!segments.length) return [];

    return segments.map((segment, index) => {
      let formattedSegment = segment;
      if (segment.length > 15) {
        formattedSegment = segment.match(/.{1,15}/g).join("\n");
      }
      return {
        option: formattedSegment,
        style: {
          backgroundColor:
            index % 2 === 0 ? cssState.backgroundColor1 : cssState.backgroundColor2,
          textColor: index % 2 === 0 ? cssState.textColor1 : cssState.textColor2,
          fontSize: formattedSegment.length > 15 ? 15 : 22,
          fontSizeInner: formattedSegment.length > 15 ? 20 : 12,
        },
      };
    });
  }, [
    segments,
    cssState.backgroundColor1,
    cssState.backgroundColor2,
    cssState.textColor1,
    cssState.textColor2,
  ]);

  // handleSpinClick fonksiyonunu güncelleyelim
  const handleSpinClick = useCallback(async () => {
    if (!mustSpin && segments.length > 0) {
      // Spin hakkı kontrolü
      if (!spinCount || spinCount <= 0) {
        setShowNoSpinAlert(true);
        return;
      }

      try {
        setIsSpinning(true);
        const userId = getUserIdFromUrl();
        const result = await spinWheel(userId);

        if (result.success) {
          const prizeIndex = segments.findIndex((segment) => segment === result.prize);

          if (prizeIndex !== -1) {
            setPrizeNumber(prizeIndex);
            setMustSpin(true);
            setSpinCount(result.remainingSpins);

            // Ses çalmayı 2 saniye geciktir
            setTimeout(() => {
              wheelSoundRef.current.currentTime = 0;
              wheelSoundRef.current.play().catch(() => {});
            }, 1250);
          } else {
            setAlertConfig({
              show: true,
              message: "Beklenmeyen bir hata oluştu",
              type: "error",
            });
          }
        } else {
          setAlertConfig({
            show: true,
            message: result.message || "Çark çevirme işlemi başarısız",
            type: "error",
          });
        }
      } catch (error) {
        setAlertConfig({
          show: true,
          message: "Çark çevirme sırasında bir hata oluştu",
          type: "error",
        });
      } finally {
        setIsSpinning(false);
      }
    }
  }, [mustSpin, segments, spinCount]);

  const handleStopSpinning = useCallback(() => {
    setMustSpin(false);
    setShowPrizeModal(true);

    // Güvenli ses geçişi: önce wheel sesini durdur, sonra kazanma sesini çal
    const wheelAudio = wheelSoundRef.current;
    const winAudio = winSoundRef.current;

    try {
      wheelAudio.pause();
      wheelAudio.currentTime = 0;
    } catch (e) {
      // ses zaten durmuş olabilir
    }

    winAudio.currentTime = 0;
    winAudio.play().catch(() => {});

    // 🎆 Havai Fişek + Konfeti Efekti
    const duration = 3000;
    const end = Date.now() + duration;

    // İlk patlama
    confetti({
      particleCount: 100,
      spread: 80,
      origin: { y: 0.6 },
      colors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96E6A1', '#DDA0DD'],
      zIndex: 9999,
    });

    // Havai fişek efekti — sol ve sağdan
    const interval = setInterval(() => {
      if (Date.now() > end) {
        clearInterval(interval);
        return;
      }
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#FFD700', '#FF4500', '#00FF88'],
        zIndex: 9999,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#FFD700', '#FF4500', '#00FF88'],
        zIndex: 9999,
      });
    }, 150);

    // Yıldız konfetisi — biraz gecikmeli
    setTimeout(() => {
      confetti({
        particleCount: 60,
        spread: 100,
        origin: { y: 0.35 },
        shapes: ['star'],
        colors: ['#FFD700', '#FFA500', '#FF6347'],
        scalar: 1.2,
        zIndex: 9999,
      });
    }, 800);
  }, []);

  const wheelProps = useMemo(
    () => ({
      mustStartSpinning: mustSpin,
      prizeNumber: prizeNumber,
      data: data,
      onStopSpinning: handleStopSpinning,
      outerBorderColor: cssState.borderColor,
      outerBorderWidth: 8,
      innerRadius: 10,
      innerBorderColor: cssState.pointerColor,
      innerBorderWidth: 2,
      radiusLineColor: cssState.radiusLineColor,
      radiusLineWidth: 1,
      fontSize: 20,
      textDistance: 60,
      spinDuration: 1.0,
      pointerProps: {
        style: {
          display: 'none',
        },
      },
    }),
    [
      mustSpin,
      prizeNumber,
      data,
      handleStopSpinning,
      cssState.borderColor,
      cssState.pointerColor,
      cssState.radiusLineColor,
    ]
  );

  const isWheelReady = data.length > 0;

  // Çark konteyneri için özel stil oluşturalım
  const wheelContainerStyle = useMemo(() => {
    // Wheel glow özelliklerini içeren stil
    if (cssState.wheelGlowColor && typeof cssState.wheelGlowOpacity === "number") {
      try {
        const wheelGlowRgba = `rgba(${getRGB(cssState.wheelGlowColor)}, ${
          cssState.wheelGlowOpacity
        })`;
        return {
          filter: `drop-shadow(0 0 50px ${wheelGlowRgba})`,
        };
      } catch (error) {
        console.error("Wheel stil oluşturma hatası:", error);
      }
    }

    // Varsayılan stil
    return {
      filter: "drop-shadow(0 0 50px rgba(9, 249, 132, 0.2))",
    };
  }, [cssState.wheelGlowColor, cssState.wheelGlowOpacity]);

  useEffect(() => {
    const root = document.documentElement;

    // CSS değişkenlerini güncelle
    root.style.setProperty("--button-gradient-1", cssState.buttonGradient1);
    root.style.setProperty("--button-gradient-2", cssState.buttonGradient2);
    root.style.setProperty("--button-text-color", cssState.buttonTextColor);
    root.style.setProperty("--button-border-color", cssState.buttonBorderColor);
    root.style.setProperty("--button-glow-color", cssState.buttonGlowColor);
    root.style.setProperty("--button-disabled-1", cssState.buttonDisabled1);
    root.style.setProperty("--button-disabled-2", cssState.buttonDisabled2);
    root.style.setProperty("--button-disabled-text", cssState.buttonDisabledText);
    root.style.setProperty("--button-disabled-border", cssState.buttonDisabledBorder);

    // Wheel glow özelliklerini güncelle
    try {
      // wheelGlowColor ve wheelGlowOpacity değerlerinin varlığını kontrol et
      if (cssState.wheelGlowColor && typeof cssState.wheelGlowOpacity === "number") {
        const wheelGlowRgba = `rgba(${getRGB(cssState.wheelGlowColor)}, ${
          cssState.wheelGlowOpacity
        })`;
        document.querySelectorAll(".wheel-container").forEach((container) => {
          container.style.filter = `drop-shadow(0 0 50px ${wheelGlowRgba})`;
        });
      } else {
        // Varsayılan değerleri kullan
        const defaultGlow = `rgba(9, 249, 132, 0.2)`;
        document.querySelectorAll(".wheel-container").forEach((container) => {
          container.style.filter = `drop-shadow(0 0 50px ${defaultGlow})`;
        });
      }
    } catch (error) {
      console.error("Wheel glow güncellenirken hata:", error);
      // Hata durumunda varsayılan değer
      const defaultGlow = `rgba(9, 249, 132, 0.2)`;
      document.querySelectorAll(".wheel-container").forEach((container) => {
        container.style.filter = `drop-shadow(0 0 50px ${defaultGlow})`;
      });
    }

    // RGB değişkenlerini güncelle
    root.style.setProperty(
      "--button-border-color-rgb",
      getRGB(cssState.buttonBorderColor)
    );
    root.style.setProperty("--button-gradient-2-rgb", getRGB(cssState.buttonGradient2));

    // Dekorasyon CSS değişkenleri
    // Ring hue-rotate (altın → hedef renk)
    if (cssState.ringAccentColor) {
      const ringHue = hexToHue(cssState.ringAccentColor);
      root.style.setProperty('--ring-hue', `${Math.round(ringHue - 51)}deg`);
    }
    // Ambient glow RGB
    if (cssState.ambientGlowColor) {
      root.style.setProperty('--ambient-glow-rgb', getRGB(cssState.ambientGlowColor));
    }
    // Stand accent RGB
    if (cssState.standAccentColor) {
      root.style.setProperty('--stand-accent-rgb', getRGB(cssState.standAccentColor));
    }
    // Clover hue-rotate
    if (cssState.cloverColor) {
      const cloverHue = hexToHue(cssState.cloverColor);
      root.style.setProperty('--clover-hue', `${Math.round(cloverHue - 47)}deg`);
    }

    // Modal CSS değişkenleri
    root.style.setProperty('--modal-bg', cssState.modalBg || `linear-gradient(145deg, ${cssState.backgroundGradient1}, ${cssState.backgroundGradient2})`);
    root.style.setProperty('--modal-border-color', cssState.modalBorderColor || cssState.borderColor);
    root.style.setProperty('--modal-glow-rgb', getRGB(cssState.modalBorderColor || cssState.borderColor));
    root.style.setProperty('--modal-header-color', cssState.modalHeaderColor || cssState.textColor1);
    root.style.setProperty('--modal-text-color', cssState.modalTextColor || cssState.textColor2);
    root.style.setProperty('--modal-btn-gradient-1', cssState.modalButtonGradient1 || cssState.buttonGradient1);
    root.style.setProperty('--modal-btn-gradient-2', cssState.modalButtonGradient2 || cssState.buttonGradient2);
    root.style.setProperty('--modal-btn-text', cssState.modalButtonText || cssState.buttonTextColor);

    // ... diğer CSS değişkenleri ...
  }, [cssState]);

  // Wheel glow özelliklerini izle ve değiştiğinde uygula
  useEffect(() => {
    // Wheel glow değiştiğinde doğrudan uygula
    if (cssState.wheelGlowColor && typeof cssState.wheelGlowOpacity === "number") {
      try {
        // CSS değişkenlerini document root'a ekle
        const wheelGlowRgba = `rgba(${getRGB(cssState.wheelGlowColor)}, ${
          cssState.wheelGlowOpacity
        })`;
        console.log("Uygulanan wheelGlow (Direkt Stil):", wheelGlowRgba);

        // Özel CSS değişkeni oluştur
        document.documentElement.style.setProperty("--wheel-glow-color", wheelGlowRgba);

        // CSS kuralı oluştur
        const styleId = "dynamic-wheel-glow";
        let styleEl = document.getElementById(styleId);

        if (!styleEl) {
          styleEl = document.createElement("style");
          styleEl.id = styleId;
          document.head.appendChild(styleEl);
        }

        // Tüm olası seçicileri kapsamak için genişletilmiş CSS
        styleEl.textContent = `
          .wheel-container {
            filter: drop-shadow(0 0 50px var(--wheel-glow-color)) !important;
          }
        `;
      } catch (error) {
        console.error("Wheel glow güncellenirken hata:", error);
      }
    }
  }, [cssState.wheelGlowColor, cssState.wheelGlowOpacity]);

  // Admin kontrolü için useEffect'i güncelle
  useEffect(() => {
    const checkAdminRoute = () => {
      if (isAdminRoute() && !isAdmin) {
        setShowAdminLogin(true);
      }
    };

    checkAdminRoute();
  }, [location.pathname, isAdmin]);

  // handleLogin fonksiyonunu güncelleyelim
  const handleLogin = () => {
    setIsAdmin(true);
    setShowAdminLogin(false);

    // Başarılı login alert'i göster
    setAlertConfig({
      show: true,
      message: "Giriş başarılı!",
      type: "success",
    });

    // Alert'i kapat ama sayfada kal
    setTimeout(() => {
      setAlertConfig({ show: false, message: "", type: "success" });
    }, 2000);
  };

  const handleAIChanges = (changes) => {
    // Her bir değişikliği uygula
    Object.entries(changes).forEach(([key, value]) => {
      handleColorChange(key, value);
    });

    // CSS değişkenlerini güncelle
    const root = document.documentElement;

    // Header arkaplan rengi
    if (changes.headerBg) {
      root.style.setProperty("--header-bg", changes.headerBg);
    }
    // Header kenar rengi
    if (changes.headerBorder) {
      root.style.setProperty("--header-border", changes.headerBorder);
    }
    // Header ikon rengi
    if (changes.headerIconColor) {
      root.style.setProperty("--header-icon-color", changes.headerIconColor);
    }
    // Diğer renk değişiklikleri...
  };

  // Popup durumlarını yönetmek için
  const handlePopupOpen = (popupName) => {
    // Tüm popup'ları kapat
    setShowWheelEditor(false);
    setShowDesignEditor(false);
    setShowAIChat(false);

    // Sadece istenen popup'ı aç
    switch (popupName) {
      case "wheel":
        setShowWheelEditor(true);
        break;
      case "design":
        setShowDesignEditor(true);
        break;
      case "chat":
        setShowAIChat(true);
        break;
      default:
        break;
    }
  };

  // Kullanıcı başlatma useEffect'ini güncelle
  useEffect(() => {
    const initializeUser = async () => {
      const userId = getUserIdFromUrl();

      // Sadece admin sayfasına erişmeye çalışıyorsa
      if (!userId && isAdminRoute()) {
        setInvalidAccess(true);
        setLoading(false);
        return;
      }

      // userId yoksa ve admin route'unda değilsek
      if (!userId && !isAdminRoute()) {
        setInvalidAccess(true);
        setLoading(false);
        return;
      }

      try {
        const validationResult = await validateUser(userId);

        if (validationResult) {
          const userData = await fetchUserData(userId);
          if (userData) {
            setUserName(userData.name);
            setSpinCount(userData.spin_count);
            setStatus(true);
          }
        } else {
          // Validasyon başarısız olduğunda
          setValidationError("Kullanıcı doğrulanamadı");
          return;
        }
      } catch (error) {
        console.error("Kullanıcı başlatma hatası:", error);
        setValidationError("Bir hata oluştu");
      } finally {
        if (!validationError) {
          setLoading(false);
        }
      }
    };

    initializeUser();
  }, []);

  useEffect(() => {
    const loadCssData = async () => {
      try {
        const cssData = await fetchCarkCss();
        if (cssData) {
          setCssState(cssData);
          console.log("API'den gelen CSS verileri:", cssData); // API verilerini kontrol et

          // CSS değişkenlerini güncelle
          Object.entries(cssData).forEach(([key, value]) => {
            // Base64 görsel ve boolean flag'ler CSS değişkeni olarak set edilmez
            if (key === "customBgImage" || key === "customBgEnabled") return;
            // Özel durumlar için kontroller
            if (key === "backgroundGradient1") {
              document.documentElement.style.setProperty("--bg-gradient-1", value);
            } else if (key === "backgroundGradient2") {
              document.documentElement.style.setProperty("--bg-gradient-2", value);
            } else if (key === "headerBg") {
              // Header bg için opacity ekle
              const rgbaValue = value.includes("rgba")
                ? value
                : value.replace(")", ", 0.7)").replace("rgb", "rgba");
              document.documentElement.style.setProperty("--header-bg", rgbaValue);
            } else if (key === "headerBorderColor" || key === "headerItemBorder") {
              // Border için opacity ekle
              const rgbaValue = value.includes("rgba")
                ? value
                : value.replace(")", ", 0.1)").replace("rgb", "rgba");
              document.documentElement.style.setProperty(
                `--${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`,
                rgbaValue
              );
            } else if (key === "buttonGradient1") {
              document.documentElement.style.setProperty("--button-gradient-1", value);
            } else if (key === "buttonGradient2") {
              document.documentElement.style.setProperty("--button-gradient-2", value);
            } else {
              // Diğer tüm değişkenler için standart dönüşüm
              const cssVarName = key.replace(/([A-Z])/g, "-$1").toLowerCase();
              document.documentElement.style.setProperty(`--${cssVarName}`, value);
            }
          });

          // Wheel glow özelliklerini doğrudan uygula
          if (cssData.wheelGlowColor && typeof cssData.wheelGlowOpacity === "number") {
            try {
              const wheelGlowRgba = `rgba(${getRGB(cssData.wheelGlowColor)}, ${
                cssData.wheelGlowOpacity
              })`;
              document.querySelectorAll(".wheel-container").forEach((container) => {
                container.style.filter = `drop-shadow(0 0 50px ${wheelGlowRgba})`;
              });
            } catch (error) {
              console.error("Wheel glow uygulanırken hata:", error);
            }
          }
        }
      } catch (error) {
        console.error("CSS yükleme hatası:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCssData();
  }, []);

  // Renk değişikliklerini sadece cssState'e uygula
  const handleColorChange = useCallback((key, value) => {
    setCssState((prev) => ({ ...prev, [key]: value }));

    // CSS değişkenlerini güncelle
    const cssVarName = key.replace(/([A-Z])/g, "-$1").toLowerCase();
    document.documentElement.style.setProperty(`--${cssVarName}`, value);

    // Özel durumlar için ek kontroller
    if (key === "headerBg") {
      // RGBA değerini hesapla (70% opaklık)
      const rgbaValue = value.replace(")", ", 0.7)").replace("rgb", "rgba");
      document.documentElement.style.setProperty("--header-bg", rgbaValue);
    }

    if (key === "headerBorderColor" || key === "headerItemBorder") {
      // RGBA değerini hesapla (10% opaklık)
      const rgbaValue = value.replace(")", ", 0.1)").replace("rgb", "rgba");
      document.documentElement.style.setProperty(`--${cssVarName}`, rgbaValue);
    }

    if (key === "backgroundGradient1") {
      document.documentElement.style.setProperty("--bg-gradient-1", value);
    }

    if (key === "backgroundGradient2") {
      document.documentElement.style.setProperty("--bg-gradient-2", value);
    }

  }, []);

  const compressBgImage = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX_W = 900;
        let { width, height } = img;
        if (width > MAX_W) {
          height = Math.round(height * MAX_W / width);
          width = MAX_W;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.55));
      };
      img.onerror = reject;
      img.src = url;
    });
  }, []);

  const handleBgImageUpload = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setAlertConfig({ show: true, message: "Lütfen geçerli bir görsel dosyası seçin", type: "error" });
      return;
    }
    try {
      const compressed = await compressBgImage(file);
      setCssState(prev => ({ ...prev, customBgImage: compressed, customBgEnabled: true }));
      setAlertConfig({ show: true, message: "Arkaplan görseli yüklendi — kaydetmek için 'CSS Kaydet' butonuna basın", type: "success" });
    } catch {
      setAlertConfig({ show: true, message: "Görsel işlenirken bir hata oluştu", type: "error" });
    }
  }, [compressBgImage]);

  const handleRemoveBgImage = useCallback(() => {
    setCssState(prev => ({ ...prev, customBgImage: null, customBgEnabled: false }));
  }, []);

  const handleResetCss = () => {
    setShowResetModal(true);
  };

  const handleConfirmReset = async () => {
    try {
      setIsLoading(true);
      setShowResetModal(false);
      const result = await resetToDefaultCss();

      if (result.success) {
        setAlertConfig({
          show: true,
          message: "Varsayılan CSS ayarlarına başarıyla dönüldü",
          type: "success",
        });

        if (result.data) {
          setCssState(result.data);
          console.log("Sıfırlama sonrası CSS verileri:", result.data);

          // CSS değişkenlerini güncelle
          Object.entries(result.data).forEach(([key, value]) => {
            if (key === "customBgImage" || key === "customBgEnabled") return;
            if (key === "headerBg") {
              const rgbaValue = value.replace(")", ", 0.7)").replace("rgb", "rgba");
              document.documentElement.style.setProperty("--header-bg", rgbaValue);
            } else if (key === "headerBorderColor" || key === "headerItemBorder") {
              const rgbaValue = value.replace(")", ", 0.1)").replace("rgb", "rgba");
              document.documentElement.style.setProperty(
                `--${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`,
                rgbaValue
              );
            } else if (key === "backgroundGradient1") {
              document.documentElement.style.setProperty("--bg-gradient-1", value);
            } else if (key === "backgroundGradient2") {
              document.documentElement.style.setProperty("--bg-gradient-2", value);
            } else if (key === "buttonGradient1") {
              document.documentElement.style.setProperty("--button-gradient-1", value);
            } else if (key === "buttonGradient2") {
              document.documentElement.style.setProperty("--button-gradient-2", value);
            } else {
              const cssVarName = key.replace(/([A-Z])/g, "-$1").toLowerCase();
              document.documentElement.style.setProperty(`--${cssVarName}`, value);
            }
          });

          // Wheel glow özelliklerini doğrudan uygula
          if (
            result.data.wheelGlowColor &&
            typeof result.data.wheelGlowOpacity === "number"
          ) {
            try {
              const wheelGlowRgba = `rgba(${getRGB(result.data.wheelGlowColor)}, ${
                result.data.wheelGlowOpacity
              })`;
              document.querySelectorAll(".wheel-container").forEach((container) => {
                container.style.filter = `drop-shadow(0 0 50px ${wheelGlowRgba})`;
              });
            } catch (error) {
              console.error("Wheel glow sıfırlama sırasında hata:", error);
            }
          }
        }
      } else {
        setAlertConfig({
          show: true,
          message: result.message || "Varsayılan CSS ayarlarına dönülemedi",
          type: "error",
        });
      }
    } catch (error) {
      console.error("CSS sıfırlama hatası:", error);
      setAlertConfig({
        show: true,
        message: "Varsayılan CSS ayarlarına dönülürken bir hata oluştu",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // history butonuna tıklandığında geçmişi yükle
  const handleHistoryClick = async () => {
    if (!mustSpin) {
      const userId = getUserIdFromUrl();
      if (userId) {
        const history = await fetchUserHistory(userId);
        if (history) {
          setHistoryData(history);
        }
        setShowHistory(true);
      }
    }
  };

  const handleSpinCountRequest = async () => {
    if (!mustSpin) {
      const userId = getUserIdFromUrl();
      if (userId) {
        setIsRequestingSpinCount(true);
        try {
          const spinCountResponse = await sendSpinCountRequest(userId);

          // API yanıtını alert ile göster
          setAlertConfig({
            show: true,
            message: spinCountResponse.message || "İşlem tamamlandı",
            type: spinCountResponse.status ? "success" : "error",
          });

          // Eğer başarılı ise spin sayısını güncelle
          if (spinCountResponse.status && spinCountResponse.data) {
            setSpinCount(spinCountResponse.data.spin_count);
          }
        } catch (error) {
          setAlertConfig({
            show: true,
            message: "Hak talep edilirken bir hata oluştu",
            type: "error",
          });
        } finally {
          setIsRequestingSpinCount(false);
        }
      }
    }
  };

  // URL'den userId'yi alan yardımcı fonksiyonu ekleyelim (zaten var ama tekrar ekleyelim)
  const getUserIdFromUrl = () => {
    const pathSegments = window.location.pathname.split("/");
    return pathSegments[1] || null;
  };

  // handleCloseLogin fonksiyonunu güncelleyelim
  const handleCloseLogin = () => {
    setShowAdminLogin(false);
    const userId = getUserIdFromUrl();

    if (userId) {
      navigate(`/${userId}`);
    } else {
      navigate("/");
    }
  };

  // Logo URL'sini oluşturan fonksiyonu güncelleyelim
  const getLogoUrl = () => {
    const baseUrl = window.location.origin; // http://localhost:3000
    return `${baseUrl}/image7.png`; // userId olmadan direkt image7.png'ye yönlendir
  };

  // useEffect içindeki logo yükleme kısmını güncelleyelim
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const logoUrl = getLogoUrl();
        const response = await fetch(logoUrl);

        if (!response.ok) {
          throw new Error("Logo yüklenemedi");
        }

        setLogoSrc(logoUrl);
      } catch (error) {
        console.error("Logo yükleme hatası:", error);
        // Logo yüklenemezse varsayılan bir logo kullanabilirsiniz
        setLogoSrc("/default-logo.png");
      }
    };

    loadLogo();
  }, []); // userId dependency'sini kaldırdık

  // Loading ve hata durumlarını kontrol et
  if (loading || validationError || isLoading) {
    return (
      <Loading error={!!validationError} message={validationError || "Yükleniyor..."} />
    );
  }

  // Geçersiz erişim durumu
  if (invalidAccess) {
    return (
      <div className="invalid-access">
        <h1>Geçersiz Erişim</h1>
        <p>Bu sayfaya doğrudan erişim sağlanamaz.</p>
      </div>
    );
  }

  return (
    <div
      className="App"
      style={{
        backgroundImage: cssState.customBgEnabled && cssState.customBgImage
          ? `url('${cssState.customBgImage}')`
          : `url('/${activeBackground}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* CSS Butonları */}
      {isAdmin && (
        <>
          <AdminButtons onSaveCss={handleSaveCssChanges} onResetCss={handleResetCss} />

          <ResetConfirmationModal
            isOpen={showResetModal}
            onConfirm={handleConfirmReset}
            onCancel={() => setShowResetModal(false)}
          />
        </>
      )}

      <header className="header">
        <div className="welcome">
          <span>👋</span> Hoşgeldin, {userName || "Kullanıcı"}
        </div>
        <div className="spin-count">
          <span>🎰</span> Çevirme Hakkı: {spinCount || 0}
        </div>
        <button
          className={`history ${mustSpin || isRequestingSpinCount ? "disabled" : ""}`}
          onClick={handleSpinCountRequest}
          disabled={mustSpin || isRequestingSpinCount}
          title="Hak Talep Et"
        >
          <span>{isRequestingSpinCount ? "⏳" : "🎁"}</span>
          {isRequestingSpinCount ? "İşleniyor..." : "Hak Talep Et"}
        </button>
        <button
          className={`history ${mustSpin ? "disabled" : ""}`}
          onClick={handleHistoryClick}
          disabled={mustSpin}
          title={mustSpin ? "Çark dönüyorken geçmişe bakamazsınız" : "Geçmiş"}
        >
          <span>📜</span>
          Geçmiş
        </button>
      </header>

      <div className="casino-scene">
        <div className="casino-wheel-wrap">
          <div className="casino-ambient-glow" />
          <div className="casino-outer-ring" />
          <div className={`wheel-container ${mustSpin ? 'is-spinning' : 'is-idle'}`} style={wheelContainerStyle}>
            {isWheelReady ? (
              <Wheel {...wheelProps} />
            ) : (
              <div style={{ color: "#fff", textAlign: "center", padding: "24px 0" }}>
                Cark verileri yukleniyor...
              </div>
            )}
            <button
              className={`spin-button ${mustSpin ? 'is-spinning' : ''}`}
              onClick={handleSpinClick}
              disabled={mustSpin || isSpinning || !isWheelReady}
            >
              <span>{mustSpin ? "BOL\nŞANSLAR" : "ÇEVİR"}</span>
            </button>
          </div>
          <div className="casino-pointer">
            <span className="casino-pointer-clover">🍀</span>
            <div className="casino-pointer-stem" />
          </div>
          {Array.from({ length: 28 }).map((_, i) => (
            <div
              key={`ring-${i}`}
              className={i % 2 === 0 ? "ring-bulb" : "ring-spade"}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: `rotate(${i * (360 / 28)}deg) translateY(var(--casino-ring-radius, -358px)) rotate(-${i * (360 / 28)}deg) translate(-50%, -50%)`,
                zIndex: 8,
                pointerEvents: "none",
                animationDelay: i % 2 === 0 ? `${(i / 2) * 0.2}s` : `${i * 0.1}s`,
              }}
            >
              {i % 2 !== 0 && "♠"}
            </div>
          ))}
        </div>
        <div className="casino-stand">
          <div className="stand-neck" />
          <div className="stand-base" />
        </div>
      </div>

      {/* Editör Butonları */}
      {isAdmin && (
        <>
          <div className="editor-buttons">
            <button
              className="editor-toggle wheel-editor-btn"
              onClick={() => handlePopupOpen("wheel")}
            >
              🎯 Çark Editörü
            </button>
            <button
              className="editor-toggle design-editor-btn"
              onClick={() => handlePopupOpen("design")}
            >
              🎨 Tasarım Editörü
            </button>
            <button
              className="editor-toggle hook-chat-btn"
              onClick={() => handlePopupOpen("chat")}
            >
              💬 Hook Chat <span className="beta-tag">Beta</span>
            </button>
          </div>

          {showThemeSelector && (
            <ThemeSelector
              onApplyTheme={(colors) => {
                Object.entries(colors).forEach(([key, value]) => {
                  handleColorChange(key, value);
                });
              }}
            />
          )}
        </>
      )}

      {/* Çark Editörü */}
      {showWheelEditor && (
        <WheelEditor
          colors={cssState}
          onColorChange={handleColorChange}
          onClose={() => setShowWheelEditor(false)}
        />
      )}

      {/* Tasarım Editörü */}
      {showDesignEditor && (
        <DesignEditor
          colors={cssState}
          onColorChange={handleColorChange}
          onClose={() => setShowDesignEditor(false)}
          customBgImage={cssState.customBgImage}
          onBgImageUpload={handleBgImageUpload}
          onBgImageRemove={handleRemoveBgImage}
        />
      )}

      {showPrizeModal && (
        <div className="modal-overlay">
          <div
            className="modal-content"
          >
            <div className="modal-header">
              <h2>
                🎉 Tebrikler!
              </h2>
              <p>
                Kazandığınız Ödül:
              </p>
            </div>
            <div className="prize-info">
              {data[prizeNumber].option.split("\n").map((line, index) => (
                <div
                  key={index}
                  className="prize-line"
                >
                  {line}
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button
                className="claim-prize"
                onClick={() => setShowPrizeModal(false)}
              >
                ÖDÜLÜ AL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="history-overlay">
          <div
            className="history-modal"
            style={{
              background: cssState.historyModalBg || "#1A2234",
              border: `1px solid ${cssState.historyModalBorder || "#09F984"}`,
            }}
          >
            <div
              className="history-header"
              style={{ background: cssState.historyHeaderBg || "#1e293b" }}
            >
              <h2 style={{ color: cssState.historyHeaderColor || "#FFFFFF" }}>
                Çark Geçmişi
              </h2>
              <button
                className="close-history"
                onClick={() => setShowHistory(false)}
                style={{ color: cssState.historyCloseColor || "#FFFFFF" }}
              >
                ×
              </button>
            </div>

            <div className="history-content">
              {historyData.map((item) => (
                <div
                  key={item.id}
                  className="history-item"
                  style={{
                    background: cssState.historyItemBg || "#2E4057",
                    border: `1px solid ${cssState.historyItemBorder || "#09F984"}`,
                  }}
                >
                  <div className="history-item-header">
                    <span
                      className="history-date"
                      style={{ color: cssState.historyDateColor || "#94A3B8" }}
                    >
                      {item.tarih}
                    </span>
                    <span
                      className={`status-badge ${item.status || "success"}`}
                      style={{
                        backgroundColor: cssState.historyBadgeBg || "#09F984",
                        color: cssState.historyBadgeText || "#1A2234",
                      }}
                    >
                      {(item.status || "success") === "success" && "✓ Tamamlandı"}
                      {item.status === "pending" && "○ Beklemede"}
                      {item.status === "failed" && "× İptal Edildi"}
                    </span>
                  </div>

                  <div className="history-item-body">
                    <div className="history-info-group">
                      <span
                        className="info-label"
                        style={{ color: cssState.historyInfoLabel || "#94A3B8" }}
                      >
                        Çark
                      </span>
                      <span
                        className="info-value"
                        style={{ color: cssState.historyInfoValue || "#E2E8F0" }}
                      >
                        Bonus Çarkı
                      </span>
                    </div>

                    <div className="history-info-group">
                      <span
                        className="info-label"
                        style={{ color: cssState.historyInfoLabel || "#94A3B8" }}
                      >
                        Kazanılan
                      </span>
                      <span
                        className="info-value"
                        style={{ color: cssState.historyInfoValue || "#E2E8F0" }}
                      >
                        {item.win}
                      </span>
                    </div>

                    <div className="history-info-group">
                      <span
                        className="info-label"
                        style={{ color: cssState.historyInfoLabel || "#94A3B8" }}
                      >
                        Durum
                      </span>
                      <span
                        className="info-value"
                        style={{ color: cssState.historyInfoValue || "#E2E8F0" }}
                      >
                        {(item.status || "success") === "success" && "Tamamlandı"}
                        {item.status === "pending" && "Beklemede"}
                        {item.status === "failed" && "İptal Edildi"}
                      </span>
                    </div>

                    <p
                      className="history-note"
                      style={{ color: cssState.historyNoteColor || "#94A3B8" }}
                    >
                      Başarılı bir şekilde {item.win} ödülü verildi.
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Admin Login Modal */}
      {showAdminLogin && <AdminLogin onLogin={handleLogin} onClose={handleCloseLogin} />}

      {showAIChat && (
        <AIChat onApplyChanges={handleAIChanges} onClose={() => setShowAIChat(false)} />
      )}

      {alertConfig.show && (
        <CustomAlert
          message={alertConfig.message}
          type={alertConfig.type}
          onClose={() => setAlertConfig({ show: false, message: "", type: "success" })}
        />
      )}

      {showNoSpinAlert && <NoSpinAlert onClose={() => setShowNoSpinAlert(false)} colors={cssState} />}



      {/* ThemeSelector'ı isAdmin kontrolü altına al */}
      {isAdmin && (
        <ThemeSelector
          onApplyTheme={(colors) => {
            Object.entries(colors).forEach(([key, value]) => {
              handleColorChange(key, value);
            });
          }}
        />
      )}

      <div className="site-footer-logo">
        <div className="footer-logo-box">
          <img src="/long.webp" alt="Long" className="footer-logo-img" />
        </div>
      </div>
    </div>
  );
}

export default App;
